import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getLeadtimeAnalysis } from '../orders.api';
import { formatLeadTime } from '../format';

interface GtinLeadtimeBlockProps {
  serviceCodes: string[];
}

/**
 * FR19a: the doorlooptijd-blok h3-4 reserved a spot for. Shows the RELEVANT stap-overgangen
 * of this item's own diensten (dienst-niveau statistics, not per-item — drill-down per klant
 * is an explicit vervolgstap per FR19a).
 */
export function GtinLeadtimeBlock({ serviceCodes }: GtinLeadtimeBlockProps) {
  const { t } = useTranslation();
  const queries = useQueries({
    queries: serviceCodes.map((serviceCode) => ({
      queryKey: ['orders', 'analytics', 'leadtime', serviceCode, null, null],
      queryFn: () => getLeadtimeAnalysis(serviceCode, {}),
      staleTime: 60 * 1000,
    })),
  });

  if (serviceCodes.length === 0) return null;

  return (
    <section aria-label={t('orders.gtin.leadtimeSectionLabel')} data-testid="gtin-leadtime-section">
      <h2 className="mb-2 text-sm font-semibold text-[var(--foreground)]">{t('orders.gtin.leadtimeSectionLabel')}</h2>
      <div className="space-y-3">
        {serviceCodes.map((serviceCode, index) => {
          const query = queries[index];
          const transitions = query?.data?.data?.transitions ?? [];
          const degraded = query?.data?.meta.degraded ?? false;
          return (
            <div data-testid={`gtin-leadtime-${serviceCode}`} key={serviceCode}>
              <p className="text-xs font-medium text-[var(--foreground-muted)]">{serviceCode}</p>
              {degraded ? (
                <p className="text-xs text-[var(--foreground-muted)]">{t('orders.analytics.leadtimeDegraded')}</p>
              ) : transitions.length === 0 ? (
                <p className="text-xs text-[var(--foreground-muted)]">{t('orders.analytics.leadtimeEmpty')}</p>
              ) : (
                <ul className="space-y-1">
                  {transitions.map((transition) => (
                    <li className="flex items-center justify-between text-xs" key={`${transition.from}-${transition.to}`}>
                      <span>{t('orders.analytics.table.transitionLabel', { from: transition.from, to: transition.to })}</span>
                      <span className="text-[var(--foreground-muted)]">
                        {transition.insufficientData || transition.averageMs == null
                          ? t('orders.analytics.insufficientData')
                          : formatLeadTime(transition.averageMs, t)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
